import classNames from 'classnames';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
// eslint-disable-next-line
// @ts-ignore
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { Scrollbars } from '../../mixins';
/* COMPONENTS */
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Flex } from '../Flex';
import { Mask } from '../Mask';
import { MDIIcon } from '../MDIIcon';
import { Popover } from '../Popover';
import { Select } from '../Select';
import Pagination from './Pagination';
import TableAction from './TableAction';
import TableCheckbox from './TableCheckbox';
/* CONSTANTS */
import { EMPTY_ARRAY, EMPTY_OBJ, REGEX_RESERVED_CHARACTERS } from '../../constants';
/* TYPES */
import Close from '@mdi/svg/svg/close.svg?react';
import ViewColumn from '@mdi/svg/svg/view-column.svg?react';
import { COLOR, FONT_SIZE, SIZE } from '../../styles';
import { type DataTableProps, type Sorting } from './DataTable.types';

// eslint-disable-next-line
// @ts-ignore
function _extractSelection(selectedData) {
  const selection = {};
  // eslint-disable-next-line
  // @ts-ignore
  selectedData.forEach((data) => {
    if (data.id) {
      // eslint-disable-next-line
      // @ts-ignore
      selection[data.id] = true;
    }
  });
  return selection;
}

// eslint-disable-next-line
// @ts-ignore
function _paginate({ data, rowsShown, pageNumber }) {
  if (typeof pageNumber === 'number' && typeof rowsShown === 'number') {
    data = data.slice(rowsShown * (pageNumber - 1), rowsShown * pageNumber);
  }

  return data;
}

// eslint-disable-next-line
// @ts-ignore
function _sortingIcon(sortingObj: any = {}, columnProps) {
  const { isDescending, prop } = sortingObj;
  if (prop === columnProps[0]) {
    if (isDescending) {
      return <span className="sorting-icon">&darr;</span>;
    }
    return <span className="sorting-icon">&uarr;</span>;
  }
}

// eslint-disable-next-line
// @ts-ignore
function _filter({ data, query, props, columns }) {
  const sanitizedQuery = query
    // eslint-disable-next-line
    // @ts-ignore
    .replaceAll(REGEX_RESERVED_CHARACTERS, (match) => `\\${match}`)
    .trim();
  const queryArray = sanitizedQuery.split(' ');
  // eslint-disable-next-line
  // @ts-ignore
  const regexArray = queryArray.map((queryItem) => new RegExp(queryItem, 'i'));

  return filter(data, (datum) => {
    // eslint-disable-next-line
    // @ts-ignore
    const renderedValues = [];
    // eslint-disable-next-line
    // @ts-ignore
    props.forEach((prop) => {
      const propColumn = find(columns, (column) => column.props?.includes(prop));
      const propColumnRenderer = propColumn?.renderer;
      const dataValue = datum[prop];
      if (
        propColumnRenderer &&
        !React.isValidElement(propColumnRenderer({ data: datum })) &&
        propColumnRenderer({ data: datum }) !== dataValue
      ) {
        // if the data rendered in the table is not the original data
        // filter the rendered value
        renderedValues.push(propColumnRenderer({ data: datum }));
      } else {
        renderedValues.push(dataValue);
      }
    });

    // eslint-disable-next-line
    // @ts-ignore
    const testString = renderedValues.join(' ').trim();
    let matchAll = true;

    // eslint-disable-next-line
    // @ts-ignore
    regexArray.forEach((regexQ) => {
      if (!regexQ.test(testString)) {
        matchAll = false;
      }
    });

    return matchAll;
  });
}

// eslint-disable-next-line
// @ts-ignore
function _sorting(data, properties, columns) {
  if (properties) {
    const sorted = sortBy(data, (item) => {
      const { prop } = properties;
      const propColumn = find(columns, (column) => column.props?.includes(prop));
      const propColumnRenderer = propColumn?.renderer;
      const dataValue = item[prop];
      let valueToSort = dataValue;

      // note: propColumnRenderer({ data: item }) is not valid for sorting Dates
      if (propColumn?.sortType && propColumn.sortType === 'date') {
        return new Date(dataValue);
      }

      if (
        propColumnRenderer &&
        !React.isValidElement(propColumnRenderer({ data: item })) &&
        propColumnRenderer({ data: item }) !== dataValue
      ) {
        // if the data rendered in the table is not the original data
        // sort the rendered value
        valueToSort = propColumnRenderer({ data: item });
      }

      return typeof valueToSort === 'string' ? valueToSort.toLowerCase() : valueToSort;
    });
    if (properties.isDescending) {
      return reverse(sorted);
    }

    return sorted;
  }
  return data;
}

/**
 * @deprecated This DataTable component is deprecated
 * Please use the ReactTable component instead if you wish to leverage useReactTable hook from @tanstack/react-table for a bunch of features like sorting, pagination, etc.
 * You can also use the BasicTable component for a simpler table implementation or build your own table using the individual Table styled components
 * Check out the Table.stories.tsx file for examples for usage of BasicTable, ReactTable AND the BuildYourOwnTable
 */
const DataTable: React.FC<DataTableProps> = memo((props) => {
  const customizerSelectorRef = useRef<HTMLElement | null>(null);
  const {
    actions = EMPTY_ARRAY,
    availableColumns = EMPTY_ARRAY,
    className,
    colorMode = 'light',
    columns = EMPTY_ARRAY,
    customizerTitle = 'Edit',
    customizerTooltip = 'Customize columns',
    id = '',
    data,
    defaultSorting,
    fetchData,
    filter,
    filterableProps,
    firstPageTitle,
    isCustomizable = false,
    isLoading: isLoadingProp = false,
    lastPageTitle,
    locale: { localeCode } = EMPTY_OBJ,
    modalClass,
    modalStyle,
    nextPageTitle,
    numberColumnTitle,
    onColumnsUpdate,
    ofPageText,
    onSelect,
    onSortingUpdate,
    paginationEnabled = false,
    prevPageTitle,
    rowsShown = 20,
    sectionTitle,
    selectedData: selectedDataProp = EMPTY_ARRAY,
    selectionFilter,
    showCheckboxes = false,
    total = 0,
    setFilteredDataRef,
    showSelectAllInfoIcon,
    selectAllInfoIconTooltip,
    hideLastPageOption,
    hideFirstPageOption,
    disableSelection,
    getSelectionTooltip,
  } = props;

  const tableId = useMemo(() => `table-${uuid()}`, []);

  // eslint-disable-next-line
  // @ts-ignore
  const getCustomizerValue = ({ customizerOptionsDict }) => {
    // eslint-disable-next-line
    // @ts-ignore
    return columns.map(({ id }) => customizerOptionsDict[id]);
  };

  // eslint-disable-next-line
  // @ts-ignore
  const getCustomizerOptions = (columns) => {
    // eslint-disable-next-line
    // @ts-ignore
    const customizerOptions = [];
    const customizerOptionsDict = {};
    // eslint-disable-next-line
    // @ts-ignore
    columns.forEach(({ header, id, isFixed, isUnique }, value) => {
      customizerOptions.push({
        isFixed,
        isUnique,
        label: header,
        value,
      });
      // eslint-disable-next-line
      // @ts-ignore
      customizerOptionsDict[id] = value;
    });
    // eslint-disable-next-line
    // @ts-ignore
    return { customizerOptions, customizerOptionsDict };
  };

  const [allSelectedPageNumber, setAllSelectedPageNumber] = useState(null);
  const [customizerOptions, setCustomizerOptions] = useState([]);
  const [customizerValue, setCustomizerValue] = useState(null);
  const [dataLength, setDataLength] = useState(0);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(
    isLoadingProp || (!!fetchData && isEmpty(data)) ? true : false,
  );
  const [pageNumber, setPageNumber] = useState(paginationEnabled ? 1 : undefined);
  const [paginatedData, setPaginatedData] = useState([]);
  const [selectedData, setSelectedData] = useState(selectedDataProp);
  const [selection, setSelection] = useState(_extractSelection(selectedDataProp));
  const [sorting, setSorting] = useState(
    defaultSorting ?? { isDescending: true, prop: 'startTime' },
  );
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  useEffect(() => {
    setSelectedData(selectedDataProp);
    setSelection(_extractSelection(selectedDataProp));
  }, [selectedDataProp]);

  useEffect(() => {
    const { dataLength, paginatedData } = getPaginatedData();
    setDataLength(dataLength);
    setPaginatedData(paginatedData);
  }, [data, sorting, filter, pageNumber]);

  useEffect(() => {
    setAllSelectedPageNumber(null);
    setIsAllSelected(false);
    setSelectedData([]);
    setSelection({});
    // eslint-disable-next-line
    // @ts-ignore
    onSelect?.([]);
    setPageNumber(1);
  }, [filter]);

  useEffect(() => {
    const { customizerOptions, customizerOptionsDict } = getCustomizerOptions(availableColumns);
    const newCustomizerValue = getCustomizerValue({ customizerOptionsDict });
    // eslint-disable-next-line
    // @ts-ignore
    setCustomizerOptions(customizerOptions);
    // eslint-disable-next-line
    // @ts-ignore
    setCustomizerValue(newCustomizerValue);
  }, [availableColumns, columns]);

  useEffect(() => {
    // eslint-disable-next-line
    // @ts-ignore
    setIsLoading(isLoadingProp);
  }, [isLoadingProp]);

  useEffect(() => {
    // eslint-disable-next-line
    // @ts-ignore
    onSortingUpdate?.(sorting);
  }, [sorting]);

  const getPaginatedData = () => {
    let dataLength = get(data, 'length');

    let filteredData = cloneDeep(data);

    if (!isEmpty(filterableProps)) {
      filteredData = _filter({
        data: filteredData,
        query: filter || '',
        props: filterableProps,
        columns,
      });
      dataLength = filteredData.length;
    }

    const newData = fetchData ? filteredData : _sorting(filteredData, sorting, columns);
    const paginatedData = fetchData ? newData : _paginate({ data: newData, rowsShown, pageNumber });

    // keep reference to filtered data in sync so that the filteredDataAction's callbacks in DataManageTable
    // get the most recent filetered data as an argument
    setFilteredDataRef?.(newData);

    return {
      dataLength,
      paginatedData,
    };
  };

  // eslint-disable-next-line
  // @ts-ignore
  const handlePageChange = async (nextPageNumber) => {
    let fetchingPageNumber;
    if (fetchData) {
      fetchingPageNumber = nextPageNumber;
      setIsLoading(true);
      // eslint-disable-next-line
      // @ts-ignore
      await fetchData({
        sortBy: sorting,
        offset: rowsShown ? rowsShown * nextPageNumber - rowsShown : 0,
        prevPageNumber: pageNumber,
        nextPageNumber,
      });
      // Check that we don't have a fetch in progress for a different page
      if (fetchingPageNumber === nextPageNumber) {
        setIsLoading(false);
        setPageNumber(nextPageNumber);
      }
    } else {
      setPageNumber(nextPageNumber);
    }
  };

  // eslint-disable-next-line
  // @ts-ignore
  const handleColumnClick = async (props) => {
    const [prop, ...additionalProps] = props;
    const sortBy: Sorting = {
      prop,
      isDescending: false,
    };

    if (additionalProps.length > 0) {
      sortBy.additionalProps = additionalProps;
    }

    if (get(sorting, 'prop') === prop) {
      // If we are already sorting this prop flip it
      // eslint-disable-next-line
      // @ts-ignore
      sortBy.isDescending = !sorting.isDescending;
    }

    if (fetchData) {
      setIsLoading(true);
      fetchData({ sortBy }).then(() => {
        setSorting(sortBy);
        setPageNumber(1);
        setIsLoading(false);
      });
    } else {
      setSorting(sortBy);
      setPageNumber(1);
    }
  };

  // eslint-disable-next-line
  // @ts-ignore
  const isSelectable = (data) => {
    return !selectionFilter || (selectionFilter && selectionFilter(data));
  };

  const isDisabled = (data: unknown) => {
    return disableSelection && disableSelection(data);
  };

  const getTooltip = (data: unknown) => {
    return getSelectionTooltip ? getSelectionTooltip(data) : undefined;
  };

  // eslint-disable-next-line
  // @ts-ignore
  const handleSelectAllCheckBoxClick = ({ value: selected }) => {
    let allSelectedPageNumber = null;
    let isAllSelected = false;
    const newSelection = {};
    // eslint-disable-next-line
    // @ts-ignore
    const newSelectedData = [];

    if (selected) {
      allSelectedPageNumber = pageNumber;
      isAllSelected = true;
      paginatedData.forEach((data) => {
        const { id } = data;
        if (isSelectable(data) && !isDisabled(data)) {
          // eslint-disable-next-line
          // @ts-ignore
          newSelection[id] = true;
          newSelectedData.push(data);
        }
      });
    }

    // eslint-disable-next-line
    // @ts-ignore
    setAllSelectedPageNumber(allSelectedPageNumber);
    setIsAllSelected(isAllSelected);
    setSelection(newSelection);
    // eslint-disable-next-line
    // @ts-ignore
    setSelectedData(newSelectedData);
    // eslint-disable-next-line
    // @ts-ignore
    onSelect?.(newSelectedData);
  };

  // eslint-disable-next-line
  // @ts-ignore
  const handleCheckboxClick = ({ data, value: selected }) => {
    const newSelectedData = cloneDeep(selectedData);
    const newSelection = clone(selection);

    if (selected) {
      // eslint-disable-next-line
      // @ts-ignore
      newSelectedData.push(data);
      // eslint-disable-next-line
      // @ts-ignore
      newSelection[data.id] = true;
    } else {
      const index = findIndex(newSelectedData, ['id', data.id]);
      // eslint-disable-next-line
      // @ts-ignore
      newSelectedData.splice(index, 1);
      // eslint-disable-next-line
      // @ts-ignore
      delete newSelection[data.id];
    }
    setIsAllSelected(false);
    setSelection(newSelection);
    setSelectedData(newSelectedData);
    // eslint-disable-next-line
    // @ts-ignore
    onSelect?.(newSelectedData);
  };

  const customizeColumns = () => {
    setShowColumnSelector(true);
    // eslint-disable-next-line
    // @ts-ignore
    customizerSelectorRef.current.focus();
  };

  const closeCustomizer = () => setShowColumnSelector(false);

  // eslint-disable-next-line
  // @ts-ignore
  const setColumnsValue = ({ value }) => {
    const columnsSelectorFields = getFieldsFromCustomizerValue(value);
    setCustomizerValue(value);
    // eslint-disable-next-line
    // @ts-ignore
    onColumnsUpdate?.(columnsSelectorFields || columns);
  };

  // eslint-disable-next-line
  // @ts-ignore
  const getFieldsFromCustomizerValue = (sourceValue) => {
    const value = sourceValue || customizerValue;
    // eslint-disable-next-line
    // @ts-ignore
    return value.map((index) => availableColumns[index]);
  };

  const setSelectorRef = useCallback((node: HTMLElement) => {
    customizerSelectorRef.current = node;
  }, []);

  // eslint-disable-next-line
  // @ts-ignore
  const getColumnClass = (type, sorting, columnProps) => {
    if (!sorting) {
      return `column-${type}`;
    }

    const { prop: sortingPropName } = sorting;
    const [columnName] = columnProps;
    const currentSorting = sortingPropName === columnName;

    return classNames(`column-${type}`, {
      active: currentSorting,
    });
  };

  const tableData = !fetchData ? paginatedData : data;
  // eslint-disable-next-line
  // @ts-ignore
  const firstRow = (pageNumber - 1) * rowsShown;

  const wrapperClass = classNames('data-table', className, `mode-${colorMode}`);
  const customizerId = `table-customizer-${tableId}`;
  // eslint-disable-next-line
  // @ts-ignore
  const actionsColWidth = actions.length * 44 - 8 + 24;

  return (
    <Wrapper className={wrapperClass} id={id} data-testid="data-table">
      {isLoading && <Mask loading={isLoading} position="absolute" />}
      <TableContainer className="table-container">
        <table>
          <thead>
            <tr>
              {numberColumnTitle && <th className="number-column">{numberColumnTitle}</th>}
              {showCheckboxes && (
                <th style={{ width: 24 }}>
                  <Checkbox
                    checked={isAllSelected && allSelectedPageNumber === pageNumber}
                    colorMode={colorMode}
                    id="dataTable-selectAll"
                    name={`${id}-dataTable-selectAll`}
                    onChange={handleSelectAllCheckBoxClick}
                    infoIconTooltip={selectAllInfoIconTooltip}
                    withInfoIcon={showSelectAllInfoIcon}
                  />
                </th>
              )}
              {
                // eslint-disable-next-line
                // @ts-ignore
                columns.map(({ sortable, props, header, width }, index) => {
                  if (sortable && props) {
                    const columnHeaderClass = getColumnClass('header', sorting, props);

                    return (
                      <th
                        key={index}
                        onClick={() => {
                          handleColumnClick(props);
                        }}
                        className={columnHeaderClass}
                        style={{ width }}
                      >
                        {header}
                        {_sortingIcon(sorting, props)}
                      </th>
                    );
                  }
                  return <th key={index}>{header}</th>;
                })
              }
              <th style={{ minWidth: actionsColWidth, width: actionsColWidth }}>
                {isCustomizable && (
                  <Flex id={customizerId} justifyContent="center">
                    <Button
                      className="table-customizer"
                      icon={<MDIIcon icon={ViewColumn} />}
                      onClick={customizeColumns}
                      text={customizerTitle}
                      tooltip={customizerTooltip}
                      type="clear"
                    />
                    <Popover
                      className="table-customizer-selector"
                      isOverlayVisible={false}
                      isOpen={showColumnSelector}
                      onClose={closeCustomizer}
                      parent={`#${customizerId}`}
                    >
                      <Button
                        className="table-customizer-selector-closer"
                        icon={<MDIIcon icon={Close} />}
                        iconOnly={true}
                        onClick={closeCustomizer}
                        type="clear"
                      />
                      <Select
                        isClearable={false}
                        isMulti={true}
                        labelText={customizerTooltip}
                        name="table-customizer-selector"
                        onChange={setColumnsValue}
                        options={customizerOptions}
                        // eslint-disable-next-line
                        // @ts-ignore
                        refFunc={setSelectorRef}
                        value={customizerValue}
                      />
                    </Popover>
                  </Flex>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((datum, index) => {
              const selectable = isSelectable(datum);
              const disabled = isDisabled(datum);
              const tooltip = getTooltip(datum);
              return (
                <tr key={`data-table-row-${index}`}>
                  {numberColumnTitle && <td className="number-column">{index + 1 + firstRow}</td>}
                  {showCheckboxes && (
                    <td>
                      {selectable && (
                        <TableCheckbox
                          // eslint-disable-next-line
                          // @ts-ignore
                          checked={selection[datum.id]}
                          colorMode={colorMode}
                          data={datum}
                          name={`${id}-data-table-row-${index}-selector`}
                          onChange={handleCheckboxClick}
                          disabled={disabled}
                          tooltip={tooltip}
                        />
                      )}
                    </td>
                  )}
                  {
                    // eslint-disable-next-line
                    // @ts-ignore
                    columns.map(({ header, id, renderer }, innerIndex) => {
                      if (!isFunction(renderer)) {
                        return;
                      }

                      const columnCellClass = getColumnClass('cell', sorting, [id]);

                      return (
                        <td key={`${index}${innerIndex}`} className={columnCellClass}>
                          {renderer({ header, id, data: datum, localeCode })}
                        </td>
                      );
                    })
                  }
                  <td>
                    {
                      // eslint-disable-next-line
                      // @ts-ignore
                      actions.map(
                        (
                          {
                            action,
                            icon,
                            popoverRenderer,
                            modalRenderer,
                            shouldRender = () => true,
                            title,
                          },
                          actionIndex,
                        ) =>
                          shouldRender(datum) && (
                            <TableAction
                              action={action}
                              data={datum}
                              dataTestId={title}
                              icon={icon}
                              id={`${index}-${actionIndex}-${tableId}`}
                              index={index}
                              key={actionIndex}
                              popoverRenderer={popoverRenderer}
                              modalRenderer={modalRenderer}
                              title={title}
                              modalStyle={modalStyle}
                              modalClass={modalClass}
                            />
                          ),
                      )
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableContainer>
      {paginationEnabled && (
        <Pagination
          firstPageTitle={firstPageTitle}
          firstRow={firstRow}
          lastPageTitle={lastPageTitle}
          // eslint-disable-next-line
          // @ts-ignore
          length={fetchData ? total : dataLength}
          nextPageTitle={nextPageTitle}
          ofPageText={ofPageText}
          onPageChange={handlePageChange}
          // eslint-disable-next-line
          // @ts-ignore
          pageNumber={pageNumber}
          paginatedLength={tableData.length}
          prevPageTitle={prevPageTitle}
          // eslint-disable-next-line
          // @ts-ignore
          rowsShown={rowsShown}
          sectionTitle={sectionTitle}
          hideLastPageOption={hideLastPageOption}
          hideFirstPageOption={hideFirstPageOption}
        />
      )}
    </Wrapper>
  );
});

// Styled Components
const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  overflow: hidden;
  height: 100%;

  &.mode-dark {
    table {
      tr {
        border-color: ${COLOR['dark-gray']};
      }

      tbody > tr:hover {
        background-color: ${COLOR['dark-gray-dark']};
      }

      th {
        color: ${COLOR['light-gray-lighter']};
      }

      td {
        color: ${COLOR['white']};
      }

      .column-header {
        &.active {
          color: ${COLOR['white']};
        }
      }

      .column-cell {
        &.active {
          background: ${COLOR['dark-gray-dark']};
        }
      }
    }
  }

  table {
    border-collapse: collapse;
    text-align: left;
    width: 100%;

    tr {
      border-bottom: 1px solid ${COLOR['light-gray-light']};
    }

    tbody > tr:hover {
      background-color: ${COLOR['light-gray-lightest']};
    }

    td,
    th {
      overflow: hidden;
      padding: ${SIZE['8']};

      &.number-column {
        padding: ${SIZE['8']} ${SIZE['8']} ${SIZE['8']} ${SIZE['16']};
      }

      > fieldset.checkbox {
        margin: 0;
      }

      .hidden {
        display: none;
      }
    }

    th {
      color: ${COLOR['dark-gray-lightest']};
      font-size: ${FONT_SIZE['14']};
      font-weight: 500;
      text-align: left;
    }

    td {
      color: ${COLOR['dark-gray-dark']};
      font-size: ${FONT_SIZE['14']};
      font-weight: 400;
      height: 40px;

      .content {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .column-header {
      cursor: pointer;

      &.active {
        font-weight: 600;
        color: ${COLOR['dark-gray']};
      }
    }

    .column-cell {
      max-width: 500px;

      &.active {
        background: ${COLOR['white-unselected']};
      }
    }
  }

  .sorting-icon {
    padding-left: 8px;
  }

  .pagination {
    align-items: center;
    color: ${COLOR['dark-gray-lightest']};
    display: flex;
    font-size: ${FONT_SIZE['14']};
    font-weight: 400;
    height: 40px;
    justify-content: flex-end;
    margin-top: ${SIZE['12']};

    > * {
      display: inline-block;
    }

    .count {
      font-weight: 200;
      min-width: 100px;
      text-align: center;
    }
  }

  .table-action-wrapper {
    display: inline-block;
    margin-left: ${SIZE['8']};

    &:first-child {
      margin-left: 0;
    }

    button.table-action {
      &,
      &.button.styled {
        color: ${COLOR['primary']};
        display: inline-block;
        line-height: unset;
        margin: 0;
        padding: ${SIZE['8']};

        &:focus,
        &:hover {
          color: ${COLOR['turquoise-darker']};
          outline: none;
        }
      }
    }
  }

  .table-customizer {
    text-transform: none;

    &.typo-uppercase {
      text-transform: uppercase;
    }
  }
`;

const TableContainer = styled.div`
  height: 100%;
  overflow: auto;

  ${Scrollbars({
    width: '0.6em',
    height: '0.6em',
    border: '0.5px',
    foregroundColor: 'rgba(150, 150, 150, 0.5)',
    backgroundColor: 'transparent',
  })};
`;

// Display Name
DataTable.displayName = 'DataTable';

export default DataTable;
